// src/screens/PushNotifications.js
// The push console. Rebuilt 2 Nov 2026 to the design the owner approved
// (Admin-push-console-mockup-v3.html).
//
// WHAT WAS WRONG WITH THE PREVIOUS VERSION
//   1. It said "Send real push notifications via Expo". It is FCM, not Expo.
//   2. Its "active pass holders" segment filtered user_passes with
//      .not('status','eq','revoked'). user_passes has NO status column
//      (verified against the live database 2 Nov 2026) and the query's error
//      was destructured away, so that segment failed silently and sent to
//      nobody. It also excluded passes with a NULL expires_at.
//   3. It reported FCM acceptance as "delivered". Google taking a message is
//      not a phone showing it, and the difference matters when the number is
//      the only evidence anyone has.
//   4. It offered no way to see or change the eight push rules that run on a
//      schedule, because until 2 Nov there was nothing to change them with.
//
// WHAT THIS IS
//   Three tabs. Send now (proposals, segments, member search, preview,
//   guardrails). Rules (every automatic push, off / propose / automatic, plus
//   the global brake). Activity (what was actually sent).
//
// Everything reads through /api/push-admin, which is admin-verified and uses
// the service role: push_rules and push_controls have RLS on with no policies
// on purpose, so the anon key this dashboard ships cannot see them.
//
// Sending forwards to api.sanoire.com/api/admin-push, NOT to this repo's
// api/send-push.js. That file is a second FCM implementation which bypasses
// quiet hours, the member's own notification preferences, the dedup index and
// localisation. It should be retired, not kept in step.

import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell } from '../components/Console';
import { Card, CardTitle, Insight, MetricCard, Grid, Pill } from '../components/UI';
import { supabase } from '../supabaseClient';

// 3 Nov 2026: a picture for a push. Android drops a notification image that is
// missing or too big (Firebase: under 1 MB), so it is re-encoded here as a
// JPEG at most 1200 px wide before upload -- whatever was picked. Same bucket
// as CampaignsHub.
const PUSH_IMAGE_BUCKET = process.env.REACT_APP_CAMPAIGN_BUCKET || 'studio-images';
async function uploadPushImage(file) {
  const bmp = await createImageBitmap(file);
  const scale = Math.min(1, 1200 / bmp.width);
  const c = document.createElement('canvas');
  c.width = Math.round(bmp.width * scale); c.height = Math.round(bmp.height * scale);
  c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
  let blob = null;
  for (const q of [0.85, 0.7, 0.55]) {
    blob = await new Promise((r) => c.toBlob(r, 'image/jpeg', q));
    if (blob && blob.size <= 900000) break;
  }
  if (!blob || blob.size > 900000) throw new Error('image is still over 900 KB after compression');
  const path = 'push-images/push-' + Date.now() + '.jpg';
  const { error } = await supabase.storage.from(PUSH_IMAGE_BUCKET).upload(path, blob, { contentType: 'image/jpeg' });
  if (error) throw new Error(error.message);
  const { data: { publicUrl } } = supabase.storage.from(PUSH_IMAGE_BUCKET).getPublicUrl(path);
  return publicUrl;
}


const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const API = '/api/push-admin';

function useIsMobile() {
  const [m, setM] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
}

async function call(action, extra) {
  const { data: { session } } = await supabase.auth.getSession();
  const r = await fetch(API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + (session?.access_token || ''),
    },
    body: JSON.stringify({ action, ...(extra || {}) }),
  });
  const d = await r.json();
  if (d.error) throw new Error(d.error + (d.hint ? ' — ' + d.hint : ''));
  return d;
}

const MODES = ['off', 'propose', 'automatic'];
const MODE_COPY = {
  off: 'Runs and sends nothing',
  propose: 'Computes its audience and waits for a human',
  automatic: 'Sends, with nobody watching',
};

// UI.js already exports a Pill ({ children, type }) with the dashboard's own
// green/red/blue/amber/teal vocabulary. Using it rather than a second local
// Pill keeps one pill style in the product. This only maps the tones this
// screen talks in onto that vocabulary.
const TONE = { ok: 'green', no: 'red', amb: 'amber', inf: 'blue', gry: 'teal' };
function Tag({ tone, children }) {
  return <Pill type={TONE[tone] || 'blue'}>{children}</Pill>;
}

function ModeSwitch({ value, disabled, onChange }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--color-border-secondary)',
      borderRadius: 7, overflow: 'hidden', flex: 'none' }}>
      {MODES.map((m) => {
        const on = value === m;
        return (
          <button key={m} type="button" disabled={disabled} onClick={() => !on && onChange(m)}
            title={MODE_COPY[m]}
            style={{ padding: '5px 11px', fontSize: '.65rem', border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
              background: on ? (m === 'automatic' ? '#819D84' : '#314F5C') : 'transparent',
              color: on ? '#fff' : 'var(--color-text-secondary)', fontWeight: on ? 600 : 400, fontFamily: F }}>
            {m}
          </button>
        );
      })}
    </div>
  );
}

function PushNotificationsBody() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('send');
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  // shared
  const [tokenCount, setTokenCount] = useState(null);
  const [rules, setRules] = useState([]);
  const [controls, setControls] = useState(null);
  const [segments, setSegments] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [history, setHistory] = useState([]);

  // compose
  const [picked, setPicked] = useState([]);       // user ids
  const [pickedLabel, setPickedLabel] = useState('');
  const [reachableCount, setReachableCount] = useState(0);
  const [tplId, setTplId] = useState('app_update');
  const [title, setTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  // 3 Nov 2026 (owner: every push carries a picture or can have one added).
  // Empty = automatic (the city's photo, else the Sanoire image); 'none' = no
  // picture; otherwise the https URL of an uploaded or pasted image.
  const [image, setImage] = useState('');
  const [uploading, setUploading] = useState(false);
  // An optional button on the notification that opens a link (3 Nov 2026).
  const [btnLabel, setBtnLabel] = useState('');
  const [btnUrl, setBtnUrl] = useState('');
  const [override, setOverride] = useState(false);
  const [sending, setSending] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, s, p, { count: tc }, { data: hist }] = await Promise.all([
        call('rules'), call('segments'), call('proposals'),
        supabase.from('push_tokens').select('*', { count: 'exact', head: true }).eq('active', true),
        supabase.from('push_notifications').select('*').order('created_at', { ascending: false }).limit(20),
      ]);
      setRules(r.rules); setControls(r.controls);
      setSegments(s.segments); setProposals(p.proposals);
      setTokenCount(tc || 0); setHistory(hist || []);
    } catch (e) { setErr(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (query.trim().length < 2) { setHits([]); return undefined; }
    const t = setTimeout(async () => {
      try { const d = await call('search', { q: query.trim() }); setHits(d.results); }
      catch (_e) { setHits([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const setMode = async (id, mode) => {
    setErr(''); setOk('');
    try { await call('setMode', { id, mode }); setOk(id + ' → ' + mode); await load(); }
    catch (e) { setErr(e.message); }
  };

  const togglePause = async () => {
    setErr(''); setOk('');
    try { await call('pause', { paused: !controls?.automatic_paused }); await load(); }
    catch (e) { setErr(e.message); }
  };

  // Deliberately not named with a "use" prefix: CRA's
  // react-hooks/rules-of-hooks treats any such identifier as a hook, and a
  // hook called from an onClick callback fails the build. The sanoire-admin
  // skill lists this exact trap and I walked into it anyway (2 Nov 2026,
  // deployment dpl_Bg1yeB38P5rZFU5ZmwVtZuorgj7L).
  const applySegment = async (seg) => {
    setErr('');
    try {
      const d = await call('segmentMembers', { id: seg.id });
      setPicked(d.ids); setReachableCount(d.reachable.length); setPickedLabel(seg.label);
    } catch (e) { setErr(e.message); }
  };

  const send = async () => {
    setErr(''); setOk(''); setSending(true);
    try {
      if (picked.length > 10 &&
          // eslint-disable-next-line no-alert
          !window.confirm('Send to ' + picked.length + ' members (' + reachableCount + ' reachable)?')) {
        setSending(false); return;
      }
      // Through /api/push-admin (same origin), not straight to SEND_API: a
      // browser fetch to api.sanoire.com is blocked at the CORS preflight
      // (admin-push answers OPTIONS with 405), so the send never left the phone.
      const payload = { recipients: picked, override };
      if (title.trim() && bodyText.trim()) { payload.title = title.trim(); payload.body = bodyText.trim(); }
      else { payload.template = tplId; }
      if (image.trim()) payload.image = image.trim();
      if (btnLabel.trim() && /^https:\/\//.test(btnUrl.trim())) payload.button = { label: btnLabel.trim(), url: btnUrl.trim() };
      const d = await call('send', payload);
      setOk('Accepted by FCM for ' + (d.delivered || 0) + ' of ' + (d.requested || 0)
        + (d.noDevice ? ' · ' + d.noDevice + ' with no device' : '')
        + (d.notEntitled ? ' · ' + d.notEntitled + ' not entitled' : ''));
      setTitle(''); setBodyText(''); setImage(''); setBtnLabel(''); setBtnUrl(''); await load();
    } catch (e) { setErr(e.message); }
    setSending(false);
  };

  const auto = rules.filter((r) => r.mode === 'automatic');
  const other = rules.filter((r) => r.mode !== 'automatic');

  const TabBtn = ({ id, children }) => (
    <button type="button" onClick={() => setTab(id)}
      style={{ padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer', fontFamily: F,
        fontSize: '.72rem', background: tab === id ? '#E0F5FC' : 'transparent',
        color: tab === id ? '#1e5d75' : 'var(--color-text-secondary)', fontWeight: tab === id ? 600 : 400 }}>
      {children}
    </button>
  );

  return (
    <div style={{ fontFamily: F }}>
      <div style={{ marginBottom: 14, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>Push</h2>
          <p style={{ fontSize: '.74rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
            Sanoire works out who needs telling. You approve. Quiet hours and each member’s
            notification settings are respected unless you override.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 3 }}>
          <TabBtn id="send">Send now</TabBtn>
          <TabBtn id="rules">Rules</TabBtn>
          <TabBtn id="activity">Activity</TabBtn>
        </div>
      </div>

      {err ? <div style={{ marginBottom: 12 }}><Insight type="warn">{err}</Insight></div> : null}
      {ok ? <div style={{ marginBottom: 12 }}><Insight type="ok">{ok}</Insight></div> : null}

      <Grid cols={3}>
        <MetricCard label="Devices that can receive" value={tokenCount === null ? '…' : tokenCount}
          sub={tokenCount === 0 ? 'nothing can be delivered yet' : 'active push tokens'}
          accent={tokenCount === 0 ? '#dc3545' : '#314F5C'} />
        <MetricCard label="Rules running automatically" value={auto.length} sub="on a schedule" accent="#819D84" />
        <MetricCard label="Automatic sending"
          value={controls?.automatic_paused ? 'Paused' : 'Live'}
          sub={controls?.automatic_paused ? 'paused by ' + (controls.paused_by || 'an admin') : 'all rules active'}
          accent={controls?.automatic_paused ? '#9F7E4F' : '#2da06a'} />
      </Grid>

      {tab === 'send' ? (
        <>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Waiting for you · {proposals.length}</CardTitle>
            {proposals.length === 0 ? (
              <Insight type="info">Nothing needs sending right now.</Insight>
            ) : proposals.map((p) => (
              <div key={p.segment} style={{ border: '1px solid var(--color-border-secondary)', borderRadius: 10,
                padding: '12px 14px', marginBottom: 9, display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: '.78rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                    {p.count} {p.rule ? p.rule.label.toLowerCase() : p.segment}
                  </div>
                  <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap', marginTop: 6 }}>
                    <Tag tone="inf">{p.count} match</Tag>
                    <Tag tone={p.reachable ? 'ok' : 'no'}>{p.reachable} reachable</Tag>
                    {p.rule ? <Tag tone={p.rule.translated ? 'ok' : 'no'}>
                      {p.rule.translated ? '16 locales' : 'English only'}</Tag> : null}
                    {p.rule ? <Tag tone="gry">{p.rule.mode}</Tag> : null}
                  </div>
                </div>
                <button type="button"
                  onClick={() => { setPicked(p.ids); setReachableCount(p.reachable);
                    setPickedLabel(p.rule ? p.rule.label : p.segment);
                    if (p.rule) setTplId(p.rule.id); }}
                  style={{ padding: '7px 14px', borderRadius: 8, background: '#314F5C', color: '#AFE1F6',
                    border: 'none', fontSize: '.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                  Review &amp; send
                </button>
              </div>
            ))}
          </Card>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            <Card>
              <CardTitle>Or choose who yourself</CardTitle>
              {segments.map((s) => (
                <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '9px 11px', border: '1px solid var(--color-border-secondary)', borderRadius: 9,
                  marginBottom: 7, opacity: s.unreachable ? 0.55 : 1 }}>
                  <div>
                    <div style={{ fontSize: '.74rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>{s.label}</div>
                    <div style={{ fontSize: '.64rem', color: 'var(--color-text-secondary)' }}>{s.desc}</div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Tag tone={s.unreachable ? 'no' : 'inf'}>{s.count}</Tag>
                    {s.unreachable ? <Tag tone="no">cannot reach</Tag> : (
                      <button type="button" onClick={() => applySegment(s)}
                        style={{ padding: '5px 11px', borderRadius: 7, background: 'transparent',
                          border: '1px solid var(--color-border-secondary)', color: 'var(--color-text-primary)',
                          fontSize: '.66rem', cursor: 'pointer', fontFamily: F }}>Use</button>
                    )}
                  </div>
                </div>
              ))}

              <div style={{ fontSize: '.64rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase',
                letterSpacing: '.05em', margin: '14px 0 5px' }}>Find a member</div>
              <input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, email, or account number"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F, outline: 'none' }} />
              {hits.map((h) => (
                <div key={h.id} onMouseDown={() => {
                    setPicked([h.id]); setReachableCount(h.has_token ? 1 : 0);
                    setPickedLabel(h.display_name || h.email); setQuery('');
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px',
                    borderBottom: '.5px solid var(--color-border-tertiary)', cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '.73rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {h.display_name || h.email}</div>
                    <div style={{ fontSize: '.63rem', color: 'var(--color-text-secondary)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {h.email} · {String(h.id).slice(0, 8)}… · {h.has_pass ? 'Passage' : 'Free'}
                      {h.app_build ? ' · build ' + h.app_build : ''}</div>
                  </div>
                  <Tag tone={h.has_token ? 'ok' : h.has_device ? 'no' : 'amb'}>
                    {h.has_token ? 'Reachable' : h.has_device ? 'No token' : 'No device'}</Tag>
                </div>
              ))}
            </Card>

            <Card>
              <CardTitle>Message</CardTitle>
              <div style={{ fontSize: '.7rem', color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                {picked.length
                  ? <>Selected <b style={{ color: 'var(--color-text-primary)' }}>{pickedLabel}</b> — {picked.length} member{picked.length === 1 ? '' : 's'}, {reachableCount} reachable</>
                  : 'Pick a proposal, a segment or a member first.'}
              </div>

              <select value={tplId} onChange={(e) => setTplId(e.target.value)}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box', marginBottom: 10,
                  border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F }}>
                {rules.filter((r) => r.title_key).map((r) => (
                  <option key={r.id} value={r.id}>{r.label}{r.translated ? '' : ' — English only'}</option>
                ))}
              </select>

              <div style={{ fontSize: '.63rem', color: 'var(--color-text-secondary)', marginBottom: 10 }}>
                A template is translated per member, into the language they read Sanoire in.
                Leave the two fields below empty to use it.
              </div>

              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80}
                placeholder="Custom title — 80 characters"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box', marginBottom: 8,
                  border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F, outline: 'none' }} />
              <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} maxLength={240} rows={2}
                placeholder="Custom body — 240 characters"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
                  border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F, resize: 'vertical', outline: 'none' }} />
              <div style={{ marginTop: 10, fontSize: '.63rem', color: 'var(--color-text-secondary)' }}>
                Picture — automatic uses the city's photo when the push is about a city, otherwise the Sanoire image.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                {[['', 'Automatic'], ['none', 'No picture']].map(([v, l]) => (
                  <button key={l} type="button" onClick={() => setImage(v)}
                    style={{ padding: '6px 10px', borderRadius: 8, fontSize: '.7rem', fontFamily: F, cursor: 'pointer',
                      border: '1px solid var(--color-border-secondary)',
                      background: image === v ? '#314F5C' : 'var(--color-background-secondary)',
                      color: image === v ? '#AFE1F6' : 'var(--color-text-primary)' }}>{l}</button>
                ))}
                <label style={{ padding: '6px 10px', borderRadius: 8, fontSize: '.7rem', fontFamily: F, cursor: 'pointer',
                  border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)' }}>
                  {uploading ? 'Uploading…' : 'Upload image'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading}
                    onChange={async (e) => {
                      const f = e.target.files && e.target.files[0]; e.target.value = '';
                      if (!f) return;
                      setUploading(true); setErr('');
                      try { setImage(await uploadPushImage(f)); } catch (x) { setErr('Image upload failed: ' + x.message); }
                      setUploading(false);
                    }} />
                </label>
              </div>
              <input value={image === 'none' ? '' : image} onChange={(e) => setImage(e.target.value)}
                placeholder="…or paste an https image link"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box', marginTop: 8,
                  border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                  color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F, outline: 'none' }} />
              {image && image !== 'none' && !/^https:\/\//.test(image) ? (
                <div style={{ fontSize: '.63rem', color: '#C0564B', marginTop: 4 }}>The link must start with https://</div>
              ) : null}
              {image && /^https:\/\//.test(image) ? (
                <img src={image} alt="" style={{ width: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 10, marginTop: 8 }} />
              ) : null}

              <div style={{ marginTop: 10, fontSize: '.63rem', color: 'var(--color-text-secondary)' }}>
                Button (optional) — shown on the notification, opens the link.
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <input value={btnLabel} onChange={(e) => setBtnLabel(e.target.value)} maxLength={30} placeholder="Label, e.g. Read more"
                  style={{ flex: '0 0 38%', padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
                    border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                    color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F, outline: 'none' }} />
                <input value={btnUrl} onChange={(e) => setBtnUrl(e.target.value)} placeholder="https://…"
                  style={{ flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8, boxSizing: 'border-box',
                    border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)',
                    color: 'var(--color-text-primary)', fontSize: '.75rem', fontFamily: F, outline: 'none' }} />
              </div>

              {(title || bodyText) ? (
                <div style={{ marginTop: 10 }}><Insight type="warn">
                  Custom text is sent exactly as typed, in one language, to everyone selected.
                  Templates translate; custom text does not.
                </Insight></div>
              ) : null}

              {(title || bodyText) ? (
                <div style={{ background: '#0d1d2b', borderRadius: 12, padding: '12px 14px', marginTop: 12 }}>
                  <div style={{ fontSize: '.56rem', color: 'rgba(175,225,246,0.4)', textTransform: 'uppercase',
                    letterSpacing: '.06em', marginBottom: 5 }}>Sanoire · now</div>
                  <div style={{ fontSize: '.8rem', fontWeight: 500, color: '#e8f4fb' }}>{title || 'Title…'}</div>
                  <div style={{ fontSize: '.72rem', color: 'rgba(175,225,246,0.65)', lineHeight: 1.4 }}>{bodyText || 'Body…'}</div>
                </div>
              ) : null}

              <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', marginTop: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)}
                  style={{ accentColor: '#9F7E4F', marginTop: 2 }} />
                <span style={{ fontSize: '.72rem', color: 'var(--color-text-primary)' }}>
                  Ignore quiet hours and notification settings
                  <span style={{ display: 'block', fontSize: '.63rem', color: 'var(--color-text-secondary)' }}>
                    For a support reply or something time-critical. Not for announcements.
                    Every override is recorded with who sent it.
                  </span>
                </span>
              </label>

              <button type="button" onClick={send}
                disabled={sending || uploading || !picked.length || (!!title.trim() !== !!bodyText.trim())
                  || (!!image && image !== 'none' && !/^https:\/\//.test(image))}
                style={{ width: '100%', marginTop: 14, padding: '10px', borderRadius: 8, border: 'none',
                  background: sending || !picked.length ? 'rgba(49,79,92,0.3)' : '#314F5C',
                  color: '#AFE1F6', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
                {sending ? 'Sending…' : picked.length
                  ? 'Send to ' + reachableCount + ' reachable of ' + picked.length
                  : 'Nobody selected'}
              </button>
              {/* The page-level banner sits above the fold on a phone; a send
                  result that only appears there reads as "nothing happened". */}
              {err ? <div style={{ marginTop: 10 }}><Insight type="warn">{err}</Insight></div> : null}
              {ok ? <div style={{ marginTop: 10 }}><Insight type="ok">{ok}</Insight></div> : null}
              <div style={{ fontSize: '.62rem', color: 'var(--color-text-secondary)', marginTop: 6 }}>
                There is no “send to everyone”. Recipients are always a named segment or a chosen list.
                More than 10 asks you to confirm.
              </div>
            </Card>
          </div>
        </>
      ) : null}

      {tab === 'rules' ? (
        <>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Running automatically · {auto.length}</CardTitle>
            <div style={{ fontSize: '.7rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
              These are live in vercel.json. Until 2 Nov 2026 they had no switch, no caps you could
              see, and no record you could read.
            </div>
            {auto.map((r) => (
              <RuleRow key={r.id} r={r} onMode={setMode} paused={!!controls?.automatic_paused} />
            ))}
          </Card>
          <Card style={{ marginBottom: 16 }}>
            <CardTitle>Off or waiting for a human · {other.length}</CardTitle>
            {other.map((r) => (
              <RuleRow key={r.id} r={r} onMode={setMode} paused={!!controls?.automatic_paused} />
            ))}
          </Card>
          <Card>
            <CardTitle>Guardrails</CardTitle>
            <ul style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', paddingLeft: 18, margin: '0 0 12px' }}>
              <li>Maximum automatic pushes per member per day: <b>{controls?.max_auto_per_day ?? 3}</b> across all rules</li>
              <li>Quiet hours honoured — held until morning in the member’s own timezone</li>
              <li>Member preferences honoured — a rule cannot override; only a human send can</li>
              <li>One per member per rule per subkey, enforced in the database</li>
              <li>A new rule defaults to Propose, never Automatic</li>
              <li>Untranslated copy cannot be set to Automatic</li>
            </ul>
            <div style={{ marginBottom: 12 }}><Insight type="warn">
              An Automatic rule sends with nobody watching. That is the point, and it is also the
              risk — the cap above is what stops a bad segment query waking every member at once.
            </Insight></div>
            <button type="button" onClick={togglePause}
              style={{ padding: '9px 16px', borderRadius: 8, border: '1px solid ' + (controls?.automatic_paused ? '#2da06a' : '#f0cccc'),
                background: 'transparent', color: controls?.automatic_paused ? '#2da06a' : '#a32b2b',
                fontSize: '.74rem', fontWeight: 600, cursor: 'pointer', fontFamily: F }}>
              {controls?.automatic_paused ? 'Resume automatic pushes' : 'Pause all automatic pushes'}
            </button>
          </Card>
        </>
      ) : null}

      {tab === 'activity' ? (
        <Card>
          <CardTitle>Sent recently</CardTitle>
          {history.length === 0 ? <Insight type="info">Nothing sent yet.</Insight> : history.map((n, i) => (
            <div key={i} style={{ padding: '9px 0', borderBottom: '.5px solid var(--color-border-tertiary)' }}>
              <div style={{ fontSize: '.74rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>{n.title}</div>
              <div style={{ fontSize: '.67rem', color: 'var(--color-text-secondary)', marginBottom: 5 }}>{n.body}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Tag tone={n.status === 'sent' ? 'ok' : n.status === 'failed' ? 'no' : 'inf'}>{n.status}</Tag>
                {n.segment ? <Tag tone="gry">{n.segment}</Tag> : null}
                {n.sent != null ? <span style={{ fontSize: '.62rem', color: 'var(--color-text-secondary)' }}>
                  {n.sent} accepted by FCM</span> : null}
                <span style={{ fontSize: '.6rem', color: 'var(--color-text-secondary)', marginLeft: 'auto' }}>
                  {n.created_at ? n.created_at.slice(0, 10) : ''}</span>
              </div>
            </div>
          ))}
          <div style={{ fontSize: '.62rem', color: 'var(--color-text-secondary)', marginTop: 10 }}>
            Accepted by FCM is the last thing Sanoire can observe. Whether the phone showed it is not
            knowable from here.
          </div>
        </Card>
      ) : null}
    </div>
  );
}

function RuleRow({ r, onMode, paused }) {
  return (
    <div style={{ border: '1px solid var(--color-border-secondary)', borderRadius: 10, marginBottom: 9 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '11px 13px', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 210 }}>
          <div style={{ fontSize: '.76rem', fontWeight: 600, color: 'var(--color-text-primary)' }}>{r.label}</div>
          <div style={{ fontSize: '.64rem', color: 'var(--color-text-secondary)' }}>
            {r.notes || r.schedule_human || '—'}{r.cron_path ? ' · ' + r.schedule_human : ''}
          </div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 5 }}>
            <Tag tone={r.translated ? 'ok' : 'no'}>{r.translated ? '16 locales' : 'English only'}</Tag>
            <Tag tone="gry">{r.audience === 'all' ? 'everyone' : 'Passage only'}</Tag>
            {r.transactional ? <Tag tone="amb">transactional</Tag> : null}
            {r.per_member_cap ? <Tag tone="gry">{r.per_member_cap} {r.cap_window}</Tag> : null}
            {paused && r.mode === 'automatic' ? <Tag tone="amb">paused</Tag> : null}
          </div>
        </div>
        <ModeSwitch value={r.mode} disabled={false} onChange={(m) => onMode(r.id, m)} />
      </div>
      {r.last_run_at ? (
        <div style={{ borderTop: '.5px solid var(--color-border-tertiary)', padding: '7px 13px',
          fontSize: '.63rem', color: 'var(--color-text-secondary)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span>Last run {r.last_run_at.slice(0, 16).replace('T', ' ')}</span>
          {r.last_matched != null ? <span>matched {r.last_matched}</span> : null}
          {r.last_accepted != null ? <span>accepted {r.last_accepted}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export default function PushNotifications(props) {
  if (props && props.embed) return <PushNotificationsBody />;
  return <ConsoleShell title="Push"><PushNotificationsBody /></ConsoleShell>;
}
